import { Product } from '@v2/shared/lib/types';
import { useSite } from '@v2/shared/lib/site';
import { cn } from '@v2/shared/lib/utils';
import { ImageBox, srcSet } from './srcSet';

/** The `border-2` below, on both sides of the box it is drawn in. */
const BORDER = 4;

/**
 * Only the cover and the name are needed, so that is all this asks for — which lets a
 * caller that holds something leaner than a whole Product use it too. Every existing
 * caller passes a full Product and satisfies this unchanged.
 */
type Props = {
  product: Pick<Product, 'name' | 'featured_image'>;
  /**
   * Where the cover is drawn: the `<img>`'s box on each size of screen, as the caller lays
   * it out. The cover comes at the size each box draws it, so a layout that changes a box
   * changes it here too.
   */
  boxes: ImageBox[];
  className?: string;
  /**
   * Product cards are the page's subject and load eagerly. A long list of covers
   * further down is not, and twenty eager requests would compete with what is.
   */
  loading?: 'eager' | 'lazy';
};

/**
 * The cover, from the image resizer at the size it is drawn. The props carry the whole
 * image; how big it is drawn is this page's business, so the size is decided here.
 */
export default function ProductImage({ product, boxes, className, loading = 'eager' }: Props) {
  const { environment } = useSite();
  const image = product.featured_image;
  const width = image.width || 774;
  const height = image.height || 1018;

  // The sandbox has no resizer: config/services/images.php points its images at the
  // site's own /shopimages/, which answers the original whatever the query and cannot
  // fetch a url, so a digital cover's frame would not load at all.
  const sized =
    environment === 'development'
      ? undefined
      : srcSet(
          image.url,
          width,
          height,
          boxes.map((box) => ({ ...box, width: box.width - BORDER, height: box.height && box.height - BORDER }))
        );

  return (
    <img
      alt={image.alt_text || product.name}
      loading={loading}
      sizes={sized?.sizes}
      srcSet={sized?.srcSet}
      src={sized ? sized.src : image.url}
      width={width}
      height={height}
      className={cn('border-2 border-slate-200 rounded-lg overflow-hidden', className)}
    />
  );
}
